import {
  IconBuildingWarehouse,
  IconCircleCheck,
  IconFileText,
  IconHash,
  IconHome,
  IconLayoutGrid,
  IconMapPin,
  IconPencil,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

function WarehousePage() {
  const { t } = useTranslation();
  const [warehouseData, setWarehouseData] = useState<any>(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    name = "",
    enable = false,
    description = "",
    address = {},
    contact = "",
  } = warehouseData || {};

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await single(orpcClient.warehouse.read([{ id: id! }]));
        setWarehouseData(result as any);
      } catch (error) {
        console.error("Error fetching warehouse data:", error);
      }
    };

    fetchData();
  }, [id]);

  const renderField = (
    label: string,
    value: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>,
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
        {value || value === 0 ? (
          value
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {name || t("warehouse.warehouseName") || "倉庫名稱"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("warehouse.view") || "查看倉庫"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                window.open(
                  `/hrMainPage/warehouseSystem/${id}`,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="cursor-pointer flex h-10 items-center gap-2 rounded-lg bg-teal-500 px-4 text-sm font-medium text-white shadow-sm hover:bg-teal-600"
            >
              <IconLayoutGrid size={18} />
              {t("warehouse.openManagementSystem") || "進入倉儲管理系統"}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/hrMainPage/editWarehouse/${id}`)}
              className="cursor-pointer flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
            >
              <IconPencil size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconBuildingWarehouse size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderField(
              t("warehouse.warehouseName") || "倉庫名稱",
              name,
              IconBuildingWarehouse,
            )}
            {renderField(
              t("warehouse.enable") || "是否啟用",
              enable
                ? t("warehouse.enabledValue") || "是"
                : t("warehouse.disabledValue") || "否",
              IconCircleCheck,
            )}
            {renderField(
              t("warehouse.contactPerson") || "聯絡人",
              contact?.name,
              IconUser,
            )}
            {renderField(
              t("warehouse.warehouseDescription") || "倉庫描述",
              description,
              IconFileText,
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconMapPin size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("warehouse.warehouseAddress") || "倉庫地址"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
            {renderField(
              t("warehouse.city") || "縣市",
              address?.city,
              IconMapPin,
            )}
            {renderField(
              t("warehouse.district") || "行政區",
              address?.district,
              IconMapPin,
            )}
            {renderField(
              t("warehouse.postalCode") || "郵遞區號",
              address?.postalCode,
              IconHash,
            )}
            {renderField(
              t("warehouse.address") || "地址",
              address?.address,
              IconHome,
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default WarehousePage;
